import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Send, Square, Plus, History, ChevronDown, Cloud, Cpu, ShieldCheck, X, Sparkles } from 'lucide-react';
import type { AgentMode, AgentSessionSummary } from '@cortex/shared';
import { Api } from '../../lib/api';
import { useAgent } from '../../store/agent';
import { useUi } from '../../store/ui';
import { useAgentSession } from '../../hooks/useAgentSession';
import { useSessionContext } from '../../hooks/sessionContext';
import { useNote } from '../../hooks/useVault';
import { AgentMarkdown } from './AgentMarkdown';
import { ToolCard } from './ToolCard';
import { VoiceControls, speakLine, spokenLine, SPEAK_KEY } from './VoiceControls';

const MODES: Array<{ key: AgentMode; label: string; hint: string }> = [
  { key: 'ASSIST', label: 'Assist', hint: 'Questions about the vault' },
  { key: 'FAULTFIND', label: 'Faultfind', hint: 'Live fault, ranked hypotheses, case note at the end' },
  { key: 'DESIGN', label: 'Design', hint: 'Link/site design with calculators' },
  { key: 'RESEARCH', label: 'Research', hint: 'Vault first, then the web, attributed' },
  { key: 'SCRIBE', label: 'Scribe', hint: 'Turn rough notes into proposals' },
];

export function AgentPanel() {
  const a = useAgent();
  const { send, cancel, decide, patch, start, open, end } = useAgentSession();
  const session = useSessionContext();
  const activeNoteId = useUi((s) => s.panes.find((p) => p.id === s.activePaneId)?.noteId ?? null);
  const note = useNote(activeNoteId);
  const providers = useQuery({ queryKey: ['providers'], queryFn: Api.agent.providers, refetchInterval: 60_000, staleTime: 30_000 });
  const [includeNote, setIncludeNote] = useState(true);
  const [showSessions, setShowSessions] = useState(false);
  const [voiceMode, setVoiceMode] = useState<'command' | 'dictation'>('command');
  const [speak, setSpeak] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SPEAK_KEY) === '1';
    } catch {
      return false;
    }
  });
  const spokenFor = useRef<string | null>(null);
  const voiceCfg = useQuery({ queryKey: ['voice-config'], queryFn: Api.voice.config, staleTime: 300_000 });
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const busy = a.status === 'RUNNING';

  // Stick to the bottom while streaming unless the user scrolled up.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [a.turns, a.calls]);

  useEffect(() => {
    const onAsk = (e: Event) => {
      const text = (e as CustomEvent<string>).detail;
      if (text) void send(text, includeNote && activeNoteId ? { currentNoteId: activeNoteId } : {});
      else input.current?.focus();
    };
    document.addEventListener('cortex:ask', onAsk);
    return () => document.removeEventListener('cortex:ask', onAsk);
  }, [send, includeNote, activeNoteId]);

  const submit = () => {
    const text = a.draft.trim();
    if (!text || busy) return;
    a.setDraft('');
    void send(text, { ...(includeNote && activeNoteId ? { currentNoteId: activeNoteId } : {}), ...(speak ? { voice: true } : {}) });
  };
  const onTranscript = (text: string, final: boolean) => {
    if (!final) return a.setDraft(text);
    if (!text) return a.setDraft('');
    a.setDraft(text);
    if (voiceMode === 'command') setTimeout(submit, 0);
    else input.current?.focus();
  };
  // Speak the agent's "Spoken:" line once per finished answer.
  useEffect(() => {
    if (!speak) return;
    const last = [...a.turns].reverse().find((t) => t.role === 'assistant' && !t.streaming && t.text);
    if (!last || spokenFor.current === last.id) return;
    const line = spokenLine(last.text);
    if (!line) return;
    spokenFor.current = last.id;
    void speakLine(line, !!voiceCfg.data?.tts);
  }, [a.turns, speak, voiceCfg.data?.tts]);

  const current = providers.data?.providers.find((p) => p.id === (a.session?.provider ?? providers.data?.default));
  const remote = current?.remote ?? true;
  const restrictedExcluded = remote && !(a.session?.allowRestricted ?? false) && session.user?.role !== 'VIEWER';
  const pct = a.usage && a.usage.contextBudget ? Math.min(100, Math.round((a.usage.contextTokens / a.usage.contextBudget) * 100)) : 0;

  return (
    <div className="agent">
      <div className="agent-head">
        <ModePicker value={a.mode} onChange={(m) => void patch({ mode: m })} disabled={busy} />
        <span className="spacer" />
        <button className="icon" title="Sessions" onClick={() => setShowSessions(!showSessions)}><History size={14} /></button>
        <button className="icon" title="New session" onClick={() => void end().then(() => start())}><Plus size={14} /></button>
      </div>
      {showSessions && <SessionList onPick={(id) => (setShowSessions(false), void open(id))} current={a.sessionId} />}

      <div className="agent-provider">
        {current ? (
          <span className={`chip ${current.ok ? (remote ? 'teal' : 'ok') : 'crit'}`} title={current.detail}>
            {remote ? <Cloud size={11} /> : <Cpu size={11} />} {current.id} · {current.model}
          </span>
        ) : (
          <span className="chip crit">no provider</span>
        )}
        {a.session?.degraded && <span className="chip warn" title="At least one answer in this session came from the fallback model">degraded</span>}
        {a.caseRef && <button className="chip crit" title="This session is working a case; every diagnostic call lands in its timeline" onClick={() => useUi.getState().setView('cases')}>{a.caseRef}</button>}
        {restrictedExcluded ? (
          <button className="chip" title="RESTRICTED notes are not sent to a cloud model unless you opt in for this session" onClick={() => void patch({ allowRestricted: true })}>
            <ShieldCheck size={11} /> restricted excluded
          </button>
        ) : (
          a.session?.allowRestricted && remote && (
            <button className="chip warn" title="RESTRICTED notes may be sent to the cloud model in this session. Click to stop." onClick={() => void patch({ allowRestricted: false })}>
              <ShieldCheck size={11} /> restricted included
            </button>
          )
        )}
        {providers.data && providers.data.providers.length > 1 && (
          <select className="provider-select" value={a.session?.provider ?? providers.data.default ?? ''} onChange={(e) => void patch({ provider: e.target.value as 'anthropic' | 'ollama' })} title="Model provider for this session" disabled={busy}>
            {providers.data.providers.map((p) => (
              <option key={p.id} value={p.id} disabled={!p.ok}>{p.id}{p.ok ? '' : ' (down)'}</option>
            ))}
          </select>
        )}
      </div>

      {a.banners.map((b, i) => (
        <div key={i} className={`pane-banner${b.kind === 'error' ? ' crit' : ''}`} style={{ borderBottom: '1px solid var(--color-border)' }}>
          <span style={{ flex: 1 }}>{b.text}</span>
          <button className="icon" onClick={() => a.dismissBanner(i)}><X size={12} /></button>
        </div>
      ))}

      <div className="agent-scroll" ref={scroller}>
        {a.turns.length === 0 && (
          <div className="agent-empty">
            <Sparkles size={18} />
            <div>Ask about a site, a link, a device or a fault. Answers cite notes as [[links]]; anything the vault does not know is said plainly.</div>
            <div className="faint" style={{ fontSize: 11 }}>{MODES.find((m) => m.key === a.mode)?.hint}</div>
          </div>
        )}
        {a.turns.map((t) => (
          <div key={t.id} className={`turn ${t.role}`}>
            {t.role === 'user' ? (
              <div className="bubble">{t.text}</div>
            ) : (
              <>
                {t.calls.map((cid) => a.calls[cid] && <ToolCard key={cid} call={a.calls[cid]} onDecide={a.calls[cid].status === 'AWAITING_APPROVAL' ? (ok) => void decide(cid, ok) : undefined} />)}
                {t.text && <AgentMarkdown text={t.text} />}
                {t.streaming && !t.text && !t.calls.length && <span className="block-cursor" />}
                {!t.streaming && t.text && t.provider && t.provider !== 'anthropic' && <div className="faint mono" style={{ fontSize: 10 }}>answered by {t.provider}</div>}
              </>
            )}
          </div>
        ))}
      </div>

      <div className="agent-compose">
        {activeNoteId && note.data && (
          <label className="ctx-toggle" title={`Send the open note (${note.data.path}) as context`}>
            <input type="checkbox" checked={includeNote} onChange={(e) => setIncludeNote(e.target.checked)} /> context: {note.data.title}
            {note.data.visibility === 'RESTRICTED' && restrictedExcluded && <span className="chip crit" style={{ fontSize: 9 }}>restricted · not sent</span>}
          </label>
        )}
        <VoiceControls onTranscript={onTranscript} mode={voiceMode} onMode={setVoiceMode} speak={speak} onSpeak={(b) => { setSpeak(b); try { localStorage.setItem(SPEAK_KEY, b ? '1' : '0'); } catch { /* ignore */ } }} />
        <div className="compose-row">
          <textarea
            ref={input}
            value={a.draft}
            rows={2}
            placeholder={busy ? 'Working…' : a.status === 'AWAITING_APPROVAL' ? 'Approve or deny the tool call above to continue' : 'Ask Cortex… (Enter to send, Shift+Enter for a new line)'}
            onChange={(e) => a.setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            disabled={a.status === 'AWAITING_APPROVAL'}
          />
          {busy ? (
            <button className="danger" title="Cancel" onClick={() => void cancel()}><Square size={14} /></button>
          ) : (
            <button className="primary" title="Send (Enter)" onClick={submit} disabled={!a.draft.trim() || a.status === 'AWAITING_APPROVAL'}><Send size={14} /></button>
          )}
        </div>
        <div className="agent-meter mono">
          <span title="Tokens in / out this session">{a.usage ? `${fmt(a.usage.tokensIn)} in · ${fmt(a.usage.tokensOut)} out` : '—'}</span>
          <span title="Estimated cost this session">{a.usage ? `$${(a.usage.costCents / 100).toFixed(2)}` : ''}</span>
          <span className="meter" title={`Context ${a.usage?.contextTokens ?? 0} / ${a.usage?.contextBudget ?? 0} tokens`}>
            <span className="bar" style={{ width: `${pct}%`, background: pct > 85 ? 'var(--warn)' : 'var(--color-primary)' }} />
          </span>
          <span>{a.session ? `${a.session.toolCalls} tools` : ''}</span>
          <span className={`light ${a.connected ? 'ok' : a.sessionId ? 'warn' : ''}`} title={a.connected ? 'live' : 'reconnecting'} />
        </div>
      </div>
    </div>
  );
}

function ModePicker({ value, onChange, disabled }: { value: AgentMode; onChange: (m: AgentMode) => void; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const cur = MODES.find((m) => m.key === value) ?? MODES[0];
  return (
    <div className="mode-picker">
      <button className="ghost" onClick={() => setOpen(!open)} disabled={disabled} title={cur.hint}>
        <span className="light teal" /> {cur.label} <ChevronDown size={12} />
      </button>
      {open && (
        <div className="ctx-menu" style={{ position: 'absolute', top: 30, left: 0 }}>
          {MODES.map((m) => (
            <button key={m.key} onClick={() => (onChange(m.key), setOpen(false))} className={m.key === value ? 'on' : ''}>
              <span style={{ minWidth: 70, fontWeight: 600 }}>{m.label}</span>
              <span className="faint" style={{ fontSize: 11 }}>{m.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SessionList({ onPick, current }: { onPick: (id: string) => void; current: string | null }) {
  const q = useQuery({ queryKey: ['agent-sessions'], queryFn: Api.agent.sessions, staleTime: 5_000 });
  const rows = useMemo(() => (q.data?.sessions ?? []) as AgentSessionSummary[], [q.data]);
  return (
    <div className="session-list">
      {rows.length === 0 && <div className="faint" style={{ padding: 8 }}>No sessions yet.</div>}
      {rows.map((s) => (
        <button key={s.id} className={`session-row${s.id === current ? ' on' : ''}`} onClick={() => onPick(s.id)}>
          <span className="t">{s.title ?? 'untitled'}</span>
          <span className="mono faint">{s.mode.toLowerCase()} · {new Date(s.updatedAt).toLocaleDateString()}</span>
        </button>
      ))}
    </div>
  );
}

function fmt(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}
